// One event's page on the website, from Basin's public events feed.
// A visitor reads what the event is here, the way they would read a blog
// post, and is only asked to sign in when they choose to book.
(() => {
  const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const CATEGORIES = { fitness: 'Fitness', climbing: 'Climbing', other: 'Community', team: 'Community' };
  // The feed's audience says who an event is for; the name rule stays for
  // classes imported from Capitan, as on the calendar.
  const YOUTH = /home ?school|youth|kids|camp|team/i;

  function parts(iso, timeZone, options) {
    const out = {};
    new Intl.DateTimeFormat('en-US', { timeZone, ...options })
      .formatToParts(new Date(iso)).forEach((part) => { out[part.type] = part.value; });
    return out;
  }

  function clock(iso, timeZone) {
    const time = parts(iso, timeZone, { hour: 'numeric', minute: '2-digit', hourCycle: 'h23' });
    const hour = Number(time.hour);
    return (hour % 12 || 12) + (time.minute === '00' ? '' : ':' + time.minute) + (hour < 12 ? ' am' : ' pm');
  }

  function dollars(cents) {
    return '$' + (cents % 100 ? (cents / 100).toFixed(2) : String(cents / 100));
  }

  function list(words) {
    return words.length < 3 ? words.join(' and ') : words.slice(0, -1).join(', ') + ' and ' + words[words.length - 1];
  }

  // Capitan names its classes in capitals; a name staff typed is left alone.
  function display(name) {
    return name === name.toUpperCase()
      ? name.toLowerCase().replace(/(^|\s)\S/g, (letter) => letter.toUpperCase()) : name;
  }

  // The photo for an event: its class type's own if one is listed, else its
  // category's, else the default. classPhotos is the section setting's text,
  // one "CLASS TYPE NAME | photo address" per line.
  function photo(typeName, category, images, classPhotos) {
    const wanted = (typeName || '').trim().toLowerCase();
    const line = (classPhotos || '').split('\n').map((entry) => entry.split('|'))
      .find((entry) => entry.length > 1 && entry[0].trim().toLowerCase() === wanted && entry[1].trim());
    return line ? line.slice(1).join('|').trim() : images[category] || images.default || '';
  }

  // Everything the page shows, worked out from one feed row. `now` is a Date.
  function view(event, now, unpricedUrl, teamUrl) {
    const zone = event.timezone;
    const day = parts(event.start_at, zone, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const when = day.weekday + ', ' + day.month + ' ' + day.day + ', ' + day.year;
    const hours = clock(event.start_at, zone) + (event.end_at ? ' to ' + clock(event.end_at, zone) : '');
    const youth = event.audience === 'youth' || YOUTH.test(event.name) || YOUTH.test(event.type_name || '');
    // A team practice is shown, never booked: the way in is joining the team.
    const practice = event.cta === 'join_team';
    const weekly = event.series && event.series.recurrence === 'weekly' && (event.series.weekdays || []).length;

    let price = null;
    if (practice) price = null;
    else if (event.price_cents === 0) price = 'Free';
    else if (event.price_cents !== null) {
      price = dollars(event.price_cents);
      if (event.member_price_cents !== null && event.member_price_cents !== event.price_cents) {
        price += ', ' + (event.member_price_cents === 0 ? 'free' : dollars(event.member_price_cents)) + ' for members';
      }
    }

    const started = new Date(event.start_at) <= now;
    let action = null;
    let note = 'You will sign in with a one-time code to book. No password needed.';
    if (started) note = 'This one has already started.';
    else if (practice) {
      action = teamUrl ? { label: 'Join the team', href: teamUrl } : null;
      note = 'Practices are for team members. See the teams and how to join.';
    } else if (event.price_cents === null) {
      action = unpricedUrl ? { label: 'See how to sign up', href: unpricedUrl } : null;
      note = 'Online booking for this one is not open yet.';
    } else if (event.is_full) action = { label: 'Full: see the waitlist', href: event.book_url };
    else action = { label: event.price_cents === 0 ? 'Save my spot' : 'Book now', href: event.book_url };

    return {
      title: display(event.name),
      category: youth ? 'Youth' : CATEGORIES[event.kind] || '',
      typeName: event.type_name || '',
      when,
      hours,
      description: event.description || '',
      facts: [
        ['When', when + ', ' + hours],
        weekly ? ['Repeats', 'Every ' + list(event.series.weekdays.map((n) => DAY_NAMES[n - 1]))] : null,
        price ? ['Price', price] : null,
        event.fitness_included ? ['Included', 'With a fitness membership or a 2-week pass'] : null,
        event.who_can_book ? ['Who can book', event.who_can_book.summary] : null,
        event.is_full && !started && !practice ? ['Spots', 'This one is full'] : null
      ].filter(Boolean),
      action,
      note
    };
  }

  function show(root, name, text) {
    const el = root.querySelector('[data-evp="' + name + '"]');
    el.textContent = text;
    el.hidden = !text;
    return el;
  }

  function render(root, page) {
    show(root, 'category', page.category);
    show(root, 'title', page.title);
    show(root, 'when', page.when + ' · ' + page.hours);
    show(root, 'description', page.description);
    const facts = root.querySelector('[data-evp="facts"]');
    facts.replaceChildren(...page.facts.map(([label, value]) => {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const detail = document.createElement('dd');
      term.textContent = label;
      detail.textContent = value;
      row.append(term, detail);
      return row;
    }));
    const button = show(root, 'action', page.action ? page.action.label : '');
    if (page.action) button.href = page.action.href;
    show(root, 'note', page.note);
    let images = {};
    try { images = JSON.parse(root.dataset.images || '{}'); } catch (e) { images = {}; }
    const image = root.querySelector('[data-evp="image"]');
    const src = photo(page.typeName, page.category, images, root.dataset.classPhotos);
    if (src) { image.src = src; image.alt = page.title; image.hidden = false; }
    document.title = page.title + ' | Basin Climbing and Fitness';
  }

  function message(root, state) {
    root.dataset.eventState = state;
    root.querySelectorAll('[data-evp-state]').forEach((el) => { el.hidden = el.dataset.evpState !== state; });
  }

  function load(root) {
    const id = new URLSearchParams(window.location.search).get('event');
    if (!id) return Promise.resolve(message(root, 'none'));
    return fetch(root.dataset.feed + '/' + encodeURIComponent(id))
      .then((response) => {
        if (response.status === 404) return null;
        if (!response.ok) throw Error('feed ' + response.status);
        return response.json();
      })
      .then((body) => {
        if (!body) return message(root, 'gone');
        render(root, view(body.event, new Date(), root.dataset.unpriced, root.dataset.team));
        message(root, 'ready');
      })
      .catch(() => message(root, 'down'));
  }

  window.BasinEventPage = { view, photo, load };
  const start = () => document.querySelectorAll('[data-event-page]').forEach(load);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
